import React, { CSSProperties, useState, useEffect } from 'react';
import { Tabs } from '@material-ui/core';
import Tab from 'components/Base/Tabs/FixedTab';
import * as Yup from 'yup';
import { Button } from 'components/Base';
import { Field, Formik } from 'formik';
import Fields from 'components/Fields';
import api from 'api';
import { Notify } from 'utils';
import { setupMarketCommentaries } from 'pages/MpsHub/utils';

interface MarketCommentaryProps {
  data: any;
  updateData: Function;
  classes: any;
}

interface MarketCommentaryType {
  id?: number;
  name: string;
  content: string;
}

const validationSchema = Yup.object().shape({
  content: Yup.string().nullable()
});

const MarketCommentary = ({
  data,
  updateData,
  classes
}: MarketCommentaryProps) => {
  const [isSaving, setIsSaving] = useState(false);
  const [tab, setTab] = useState(0);
  const [sourceMarketCommentaries, setSourceMarketCommentaries] = useState(
    data.market_commentaries
  );
  const [marketCommentaries, setMarketCommentaries] = useState([]);
  const [currentMarketCommentary, setCurrentMarketCommentary] = useState(
    null as MarketCommentaryType | null
  );

  useEffect(() => {
    setMarketCommentaries(
      setupMarketCommentaries(sourceMarketCommentaries, true)
    );
  }, [sourceMarketCommentaries]);

  useEffect(() => {
    setCurrentMarketCommentary(marketCommentaries[tab]);
  }, [marketCommentaries, tab]);

  const findMarketCommentaryIndex = (id: number) => {
    return sourceMarketCommentaries.findIndex(
      (marketCommentary: MarketCommentaryType) => marketCommentary.id === id
    );
  };

  const tabProps = (index: any) => {
    return {
      id: `simple-tab-${index}`,
      'aria-controls': `simple-tabpanel-${index}`,
      style: {
        borderBottom: '2px solid #eaeaea',
        textTransform: 'none'
      } as CSSProperties
    };
  };

  const handleTabChange = (event: React.ChangeEvent<{}>, newValue: number) => {
    setTab(newValue);
  };

  return (
    <>
      <Tabs
        style={{
          margin: '1rem 0',
          borderBottom: '1px solid #d4d4d4',
          padding: '0 1rem'
        }}
        value={tab}
        onChange={handleTabChange}
        TabIndicatorProps={{
          style: { display: 'none' }
        }}
      >
        {marketCommentaries.map((marketCommentary: any, index: number) => (
          <Tab key={index} label={marketCommentary.name} {...tabProps(index)} />
        ))}
      </Tabs>
      <Formik
        enableReinitialize
        initialValues={{
          id: currentMarketCommentary?.id,
          name: currentMarketCommentary?.name,
          content: currentMarketCommentary?.content
        }}
        onSubmit={values => {
          setIsSaving(true);

          let method = null;
          const type = values.id ? 'update' : 'create';
          if (type === 'create') {
            method = api.createMarketCommentary(data.id, values);
          } else {
            method = api.updateMarketCommentary(
              data.id,
              values.id as number,
              values
            );
          }

          method
            .then((response: any) => {
              Notify.success(
                `${values.name} Market Commentary updated successfully.`
              );

              const index = findMarketCommentaryIndex(response.data.id);
              const marketCommentaries = [...sourceMarketCommentaries];

              if (index === -1) {
                marketCommentaries.push(response.data);
              } else {
                marketCommentaries.splice(index, 1, response.data);
              }

              setSourceMarketCommentaries(marketCommentaries);
              updateData({ ...data, market_commentaries: marketCommentaries });
            })
            .catch(() => {
              Notify.error(
                `${values.name} Market Commentary could not be updated.`
              );
            })
            .finally(() => {
              setIsSaving(false);
            });
        }}
        validationSchema={validationSchema}
      >
        {({ handleSubmit }) => {
          return (
            <form onSubmit={handleSubmit}>
              <div>
                <div className={`${classes.row} ${classes.alignBaseline}`}>
                  <div className={classes.label}>Content</div>
                  <div className={classes.fieldWrapper}>
                    <Field
                      style={{ background: '#fafafa' }}
                      name="content"
                      component={Fields.v2.WYSIWYGEditor}
                      filled
                    />
                  </div>
                </div>
              </div>
              <div>
                <Button type="submit" disabled={isSaving} isLoading={isSaving}>
                  Save
                </Button>
              </div>
            </form>
          );
        }}
      </Formik>
    </>
  );
};

export default MarketCommentary;
